import { representativeInheritancesOf } from './representative-inheritances.util';
import type { OrganizationMemberships } from './representative-inheritances.util';

export type { OrganizationMemberships } from './representative-inheritances.util';

interface RoleRepresentative {
  readonly personId: string;
  readonly contextualRoleIds?: readonly string[] | null;
}

export interface RoleActor {
  readonly kind: string;
  readonly thirdPartyId: string;
  readonly contextualRoleIds?: readonly string[] | null;
  readonly representatives?: readonly RoleRepresentative[] | null;
}

// The roles a person carries through an actor, as the back computes them
// (libs/server/access-policies, contextual-role-ids.util): a person actor its own roles; a
// representative its own roles, else the ones of its active membership of the organization.
const carriedRoleIdsOf = (
  actor: RoleActor,
  organizations: readonly OrganizationMemberships[],
  enabledStatusId: string,
): readonly string[] =>
  actor.representatives
    ? representativeInheritancesOf(
        actor,
        organizations,
        enabledStatusId,
      ).flatMap(({ contextualRoleIds }) => contextualRoleIds)
    : (actor.contextualRoleIds ?? []);

// The roles the workflow requires that an internal actor does not carry: each internal actor
// carries them all, an organization through its representatives collectively, a person through
// its own roles.
export const missingRequiredRoleIds = ({
  internalActors,
  organizations,
  enabledStatusId,
  requiredRoleIds,
}: Readonly<{
  internalActors: readonly RoleActor[];
  organizations: readonly OrganizationMemberships[];
  enabledStatusId: string;
  requiredRoleIds: readonly string[];
}>): readonly string[] => [
  ...new Set(
    internalActors.flatMap((actor) => {
      const carried = new Set(
        carriedRoleIdsOf(actor, organizations, enabledStatusId),
      );

      return requiredRoleIds.filter((roleId) => !carried.has(roleId));
    }),
  ),
];
